import type { AssetPlaybackBlocker } from "@shared/channel-playability";
import type { RightsIssue } from "@shared/content-rights";
import type { ImageIssue } from "@shared/vod-images";
import type { LocalisedIssue } from "@shared/vod-localised";
import type { DurationIssue, YearIssue } from "@shared/vod-title";
import type { CarouselIssue } from "@shared/carousel";
import type { PackageFinding } from "@shared/ingest-package";
import type {
  CatalogueProblem,
  CatalogueProblemCode,
} from "@shared/catalogue-health";
import { translateKey, type Translate } from "../../i18n/translate";

/**
 * The coded verdicts, in words (`BE-15`).
 *
 * Every one of these is DERIVED - by `assetPlaybackBlockers`, `rightsIssues`,
 * `imageIssues`, `localisedIssues`, `yearIssues`, `durationIssues` - and none of
 * them is stored anywhere. This module is the only place that turns a code into
 * a sentence, so a list row and the dialog that edits it cannot describe the
 * same finding two different ways.
 *
 * A code with no case here still SHOWS - as its own code - rather than
 * disappearing. A finding that vanishes because nobody wrote its copy is the
 * same failure as a field silently dropped on save: the row looks fine and is
 * not.
 */

const F = "content:findings.";

export function blockerText(
  t: Translate,
  blocker: AssetPlaybackBlocker
): string {
  const k = (name: string, params?: Record<string, unknown>) =>
    translateKey(t, F + "blocker." + name, params);
  switch (blocker.code) {
    case "no_playable_media":
    case "encrypted_without_drm_content_id":
    case "encrypted_without_dash_resource":
    case "drm_content_id_unverified":
      return k(blocker.code);
    case "media_has_no_resources":
      return blocker.mediaName
        ? k("media_has_no_resources_named", {
            media: blocker.mediaIndex + 1,
            name: blocker.mediaName,
          })
        : k("media_has_no_resources", { media: blocker.mediaIndex + 1 });
    case "resource_url_invalid":
      return k(blocker.code, {
        media: blocker.mediaIndex + 1,
        stream: blocker.resourceIndex + 1,
        url: blocker.url,
      });
    case "resource_protocol_unknown":
      return k(blocker.code, {
        media: blocker.mediaIndex + 1,
        stream: blocker.resourceIndex + 1,
      });
    default:
      return (blocker as { code: string }).code;
  }
}

const PROBLEM_LABELS: ReadonlySet<string> = new Set([
  "title_no_media",
  "title_not_playable",
  "channel_not_playable",
  "channel_no_epg",
  "channel_no_artwork",
  "channel_no_localised_text",
  "channel_untitled_language",
  "channel_no_description",
]);

/** A content-health filter chip: what the count next to it is a count OF. */
export function catalogueProblemLabel(
  t: Translate,
  code: CatalogueProblemCode
): string {
  return PROBLEM_LABELS.has(code)
    ? translateKey(t, F + "problemLabel." + code)
    : String(code);
}

/** One content-health problem on one row, in words. */
export function catalogueProblemText(
  t: Translate,
  problem: CatalogueProblem
): string {
  const k = (name: string, params?: Record<string, unknown>) =>
    translateKey(t, F + "problem." + name, params);
  switch (problem.code) {
    case "title_no_media":
    case "channel_no_epg":
    case "channel_no_artwork":
    case "channel_no_localised_text":
    case "channel_no_description":
      return k(problem.code);
    case "title_not_playable":
    case "channel_not_playable":
      // The first blocker is the one to fix first; the editor lists the rest.
      return problem.blockers?.[0]
        ? blockerText(t, problem.blockers[0])
        : k("cannotBePlayed");
    case "channel_untitled_language": {
      const languages = (problem.languages ?? []).join(", ");
      return languages
        ? k("channel_untitled_language", { languages })
        : k("channel_untitled_language_unknown");
    }
    default:
      return (problem as { code: string }).code;
  }
}

export function rightsIssueText(t: Translate, issue: RightsIssue): string {
  switch (issue.code) {
    case "encrypted_flag_disagrees_with_media":
    case "no_market_available_now":
    case "expires_within_30_days":
      return translateKey(t, F + "rights." + issue.code);
    default:
      return issue.code;
  }
}

export function imageIssueText(t: Translate, issue: ImageIssue): string {
  const k = (name: string, params?: Record<string, unknown>) =>
    translateKey(t, F + "image." + name, params);
  switch (issue.kind) {
    case "no_images":
      return k(issue.kind);
    case "unstorable_language":
    case "undelivered_language":
      return k(issue.kind, { language: issue.language });
    case "unknown_type":
    case "empty_url":
    case "absolute_url":
    case "unmeasured_image":
      return k(issue.kind, { language: issue.language, type: issue.type });
    case "unreachable_images":
      return k(issue.kind, { count: issue.count, language: issue.language });
    default:
      return (issue as { kind: string }).kind;
  }
}

export function localisedIssueText(
  t: Translate,
  issue: LocalisedIssue
): string {
  const k = (name: string, params?: Record<string, unknown>) =>
    translateKey(t, F + "localised." + name, params);
  switch (issue.kind) {
    case "no_localised_text":
      return k(issue.kind);
    case "blank_language":
    case "unstorable_language":
      return k(issue.kind, { language: issue.language });
    case "incomplete_language":
      return k(issue.kind, {
        language: issue.language,
        missing: issue.missing.join(", "),
      });
    default:
      return (issue as { kind: string }).kind;
  }
}

export function yearIssueText(t: Translate, issue: YearIssue): string {
  switch (issue.code) {
    case "year_only_in_release_year":
      return translateKey(t, F + "year." + issue.code, {
        releaseYear: issue.releaseYear,
      });
    case "years_disagree":
      return translateKey(t, F + "year." + issue.code, {
        productionYear: issue.productionYear,
        releaseYear: issue.releaseYear,
      });
    default:
      return (issue as { code: string }).code;
  }
}

export function durationIssueText(t: Translate, issue: DurationIssue): string {
  const k = (name: string, params: Record<string, unknown>) =>
    translateKey(t, F + "duration." + name, params);
  const mediaMinutes = Math.round(issue.mediaSeconds / 60);
  switch (issue.code) {
    case "duration_may_be_minutes":
    case "duration_disagrees_with_media":
      return k(issue.code, {
        assetSeconds: issue.assetSeconds,
        mediaSeconds: issue.mediaSeconds,
        mediaMinutes,
      });
    case "duration_missing":
      return k(issue.code, { mediaMinutes });
    default:
      return (issue as { code: string }).code;
  }
}

/**
 * A carousel's findings, in words (`BE-28`).
 *
 * Every one describes a row the portal can already produce, so none of them
 * refuses a save — they are reported. That is the deviation rule applied to the
 * UI: a copied row that arrives with one must still be editable.
 */
export function carouselFindingText(
  t: Translate,
  issue: CarouselIssue
): string {
  const k = (name: string, params?: Record<string, unknown>) =>
    translateKey(t, F + "carousel." + name, params);
  switch (issue.code) {
    case "name_missing":
    case "title_missing":
    case "curated_no_assets":
    case "query_no_filters":
    case "epg_no_channel":
    case "epg_assets_ignored":
    case "display_limit_not_positive":
      return k(issue.code);
    case "title_blank_text":
    case "title_duplicate_language":
      return issue.subject
        ? k(issue.code, { language: issue.subject.toUpperCase() })
        : k(issue.code + "_unknown");
    case "type_not_rendered":
      return k(issue.code, { type: issue.subject });
    default:
      return (issue as { code: string }).code;
  }
}

const PACKAGE_FINDINGS: ReadonlySet<string> = new Set([
  "package_empty",
  "no_video_files",
  "mixed_layout",
  "folder_without_video",
  "folder_with_several_videos",
  "unpaired_file",
  "global_metadata_unreadable",
  "metadata_unreadable",
  "metadata_not_an_object",
  "unknown_asset_type",
  "source_type_not_allowed",
  "unknown_image_type",
  "invalid_code",
  "invalid_duration",
  "title_too_long",
  "duplicate_localization_language",
  "blank_localization_field",
  "image_file_missing",
  "subtitle_file_missing",
  "subtitle_language_required",
  "localization_language_required",
  "unknown_provider",
  "ambiguous_provider",
  "unknown_entitlement",
  "provider_from_account",
  "packages_decided_by_screening",
  "supplier_not_bound",
  "subtitles_are_labels_upstream",
]);

/**
 * A package finding, in words (`BE-31`).
 *
 * The mapper emits CODES rather than prose precisely so this can exist once:
 * the portal writes free-text warnings, which cannot be counted, filtered, or
 * tested without asserting on English. An unknown code still shows AS its code
 * — a finding that vanished because nobody wrote its sentence is a package that
 * looks clean and is not.
 */
export function packageFindingText(
  t: Translate,
  finding: PackageFinding
): string {
  const code = (finding as { code: string }).code;
  if (!PACKAGE_FINDINGS.has(code)) return code;
  const ctx = (finding.context ?? {}) as Record<string, unknown>;
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(ctx))
    params[key] = String(value ?? "");
  return translateKey(t, F + "package." + code, params);
}

/**
 * The server's coded refusals, in words: `errors:<code>`, else `fallback`.
 *
 * Only the codes a person can act on from inside a dialog have a sentence.
 * Anything else falls through to the fallback, which is still better than
 * "something went wrong": the code is greppable and the hint travels with it
 * in the response body.
 */
export function saveErrorText(
  t: Translate,
  code: string | undefined,
  fallback: string
): string {
  if (!code) return fallback;
  return translateKey(t, "errors:" + code, { defaultValue: fallback });
}

/**
 * A guide read's refusal, in words (`BE-27`).
 *
 * The EPG routes are the one place in Distribution where a refusal arrives with
 * a `detail` **already written for the person reading it** - the window came out
 * of a query string, so `epg_window_inverted` says which way round the two
 * instants are and `epg_too_many_channels` says what the cap is and why. That
 * sentence is rendered as it came rather than paraphrased here, because a second
 * wording in this file is a second wording that can drift from the rule actually
 * enforcing it.
 *
 * Everything else - `content_api_unreachable`, `content_api_error` - is the same
 * refusal the lists already word, so it goes through {@link saveErrorText}. That
 * matters more here than anywhere: an unreachable catalogue must read as "the
 * catalogue is down", never as "this channel has no guide".
 */
export function epgErrorText(
  t: Translate,
  error: {
    code?: string;
    detail?: string;
    message: string;
  }
): string {
  const detail = typeof error.detail === "string" ? error.detail.trim() : "";
  if (error.code?.startsWith("epg_") && detail) return detail;
  return saveErrorText(t, error.code, error.message);
}
